import { useState } from 'react'
import { ROLES, type Role } from '../auth/types'
import { useAuth } from '../auth/useAuth'
import { useChangeRole, useSetActive, useUsers } from '../features/users/api'
import { ApiError } from '../api/client'

export function UsersPage() {
  const { user: currentUser } = useAuth()
  const { data: users, isPending, isError, error } = useUsers()
  const changeRole = useChangeRole()
  const setActive = useSetActive()
  const [actionError, setActionError] = useState<string | null>(null)

  if (isPending) {
    return <p>Loading users...</p>
  }

  if (isError) {
    return (
      <p role="alert" className="error">
        {error instanceof ApiError ? error.detail : 'Could not load users.'}
      </p>
    )
  }

  // The server refuses to remove the last Admin regardless of what the UI shows;
  // surfacing its message is how the user learns why.
  const report = (err: unknown) =>
    setActionError(err instanceof ApiError ? err.detail : 'That change could not be applied.')

  return (
    <section>
      <h1>Users</h1>
      <p className="muted">
        Anyone your identity provider authenticates can sign in and is added here as a Member.
        Promote people who need to author templates or run event plans.
      </p>

      {actionError ? (
        <p role="alert" className="error">
          {actionError}
        </p>
      ) : null}

      <div className="table-scroll">
        <table>
          <caption className="visually-hidden">Users, their roles, and access</caption>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Email</th>
              <th scope="col">Role</th>
              <th scope="col">Last signed in</th>
              <th scope="col">Access</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className={user.isActive ? undefined : 'row-inactive'}>
                <td>
                  {user.displayName}
                  {user.id === currentUser?.id ? <span className="badge">you</span> : null}
                </td>
                <td>{user.email}</td>
                <td>
                  <label className="visually-hidden" htmlFor={`role-${user.id}`}>
                    Role for {user.displayName}
                  </label>
                  <select
                    id={`role-${user.id}`}
                    value={user.role}
                    disabled={changeRole.isPending}
                    onChange={(event) => {
                      setActionError(null)
                      changeRole
                        .mutateAsync({ id: user.id, role: event.target.value as Role })
                        .catch(report)
                    }}
                  >
                    {ROLES.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  {user.lastSignInAt ? new Date(user.lastSignInAt).toLocaleDateString() : 'Never'}
                </td>
                <td>
                  <button
                    type="button"
                    disabled={setActive.isPending}
                    onClick={() => {
                      setActionError(null)
                      setActive.mutateAsync({ id: user.id, isActive: !user.isActive }).catch(report)
                    }}
                  >
                    {user.isActive ? 'Deactivate' : 'Reactivate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}
